import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, Loader2, Plus, MessageSquare } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";

/*
Step types:
- intro / submit
- text / number  (single free-text)
- select         (single choice)
- multi          (multiple, capped by `max`)
- chips          (open list — already free)
Every question with a key supports an optional "Other" free-text and an
optional "Add a note" textarea to add extra context.
*/

const STEPS = [
  { key: "intro", type: "intro" },
  { key: "child_name", label: "What's your child's first name?", type: "text", placeholder: "e.g. Ananya" },
  { key: "child_age", label: "How old is your child?", type: "number", min: 2, max: 18, placeholder: "e.g. 8" },
  { key: "country", label: "Where do you live?", type: "select", options: ["India", "USA", "UK", "UAE", "Singapore", "Australia"], allow_other: true },
  { key: "city", label: "Which city?", type: "text", placeholder: "e.g. Bengaluru" },
  { key: "language", label: "Which languages does your family use at home?", subtitle: "Pick up to 3 — including any you use only occasionally.",
    type: "multi", max: 3, allow_other: true,
    options: ["English", "Hindi", "Tamil", "Telugu", "Kannada", "Marathi", "Bengali", "Malayalam", "Gujarati", "Punjabi", "Assamese", "Odia", "Urdu"],
  },
  { key: "current_education", label: "Current education setup?", type: "select",
    options: ["Not yet in school", "Traditional school", "Homeschool", "Online school", "Mixed / Hybrid"], allow_other: true },
  { key: "learning_style", label: "How does your child learn best?", subtitle: "Pick up to 3 — most kids blend styles.",
    type: "multi", max: 3,
    options: ["Visual (pictures, diagrams)", "Auditory (talking, listening)", "Hands-on / kinesthetic", "Reading & writing", "Not sure yet"] },
  { key: "attention_span", label: "How is your child's attention span?", type: "select",
    options: ["Very short (<10 min)", "Short (10-20 min)", "Average (20-40 min)", "Long (40+ min)"] },
  { key: "interests", label: "What are your child's biggest interests?", subtitle: "Pick from below or add your own — no limit.",
    type: "chips",
    suggestions: ["Reading", "Math", "Art", "Music", "Science", "Sports", "Coding", "Robotics", "Animals", "Nature", "Dance", "Writing", "Space", "Gaming", "Cooking"] },
  { key: "personality", label: "How would you describe their personality?", type: "select",
    options: ["Very outgoing", "Somewhat outgoing", "Balanced", "Reserved", "Very introverted"], allow_other: true },
  { key: "current_skills", label: "What skills has your child already developed?", subtitle: "Pick from below or add your own.",
    type: "chips",
    suggestions: ["Reading well", "Basic math", "Advanced math", "Writing", "Public speaking", "Musical instrument", "Sports", "Second language", "Coding", "Art"] },
  { key: "parent_availability", label: "How much time can you dedicate to guiding learning each day?", type: "select",
    options: ["Less than 1 hour", "1-2 hours", "2-4 hours", "4-6 hours", "6+ hours (I can be primary teacher)"] },
  { key: "budget", label: "Monthly budget for education (excluding food/rent)?", type: "select",
    options: ["Under ₹3,000", "₹3,000 - ₹10,000", "₹10,000 - ₹25,000", "₹25,000 - ₹50,000", "Above ₹50,000"] },
  { key: "family_goals", label: "What matters most to you as a family?", subtitle: "Pick up to 3 — most families balance a few.",
    type: "multi", max: 3, allow_other: true,
    options: ["Strong academics", "Happiness & wellbeing", "Values & character", "Creativity & self-expression", "Career preparation", "Physical health & sport", "Spiritual grounding", "Community & service"] },
  { key: "academic_goals", label: "Academic ambitions for your child?", subtitle: "Pick up to 2.",
    type: "multi", max: 2, allow_other: true,
    options: ["Top-tier universities (India)", "Top-tier universities (Global)", "Good university", "Skill-based / vocational", "Entrepreneurship track", "Undecided / open"] },
  { key: "career_aspirations", label: "Any early career leanings?", subtitle: "Free text — write anything. Or 'not sure yet'.",
    type: "text", placeholder: "e.g. doctor, engineer, artist, or 'exploring'" },
  { key: "gifted", label: "Do you feel your child is gifted or advanced?", type: "select",
    options: ["Yes", "Maybe", "No", "Not sure"] },
  { key: "learning_difficulties", label: "Any learning differences or challenges?", subtitle: "Pick any that apply — optional.",
    type: "multi", max: 3, allow_other: true,
    options: ["None", "Mild focus issues", "Dyslexia / dysgraphia", "ADHD", "On the spectrum", "Speech / language delay", "Sensory issues", "Anxiety", "Prefer not to say"] },
  { key: "submit", type: "submit" },
];

export default function Assessment() {
  const [step, setStep] = useState(0);
  const [data, setData] = useState({ interests: [], current_skills: [] });
  const [chipInput, setChipInput] = useState("");
  const [otherInput, setOtherInput] = useState("");
  const [showNote, setShowNote] = useState({});
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const current = STEPS[step];
  const progress = Math.round(((step) / (STEPS.length - 1)) * 100);
  const questionSteps = STEPS.filter((s) => s.type !== "intro" && s.type !== "submit");
  const questionIdx = questionSteps.indexOf(current);
  const stepLabel = current.type === "intro"
    ? "Welcome"
    : current.type === "submit"
      ? "Almost done"
      : `Question ${questionIdx + 1} of ${questionSteps.length}`;

  const next = () => { setOtherInput(""); setStep((s) => Math.min(s + 1, STEPS.length - 1)); };
  const prev = () => { setOtherInput(""); setStep((s) => Math.max(s - 1, 0)); };

  const setField = (key, val) => setData((d) => ({ ...d, [key]: val }));

  // Multi-select helpers
  const toggleMulti = (key, val, max) => {
    const arr = Array.isArray(data[key]) ? data[key] : [];
    if (arr.includes(val)) setField(key, arr.filter((v) => v !== val));
    else if (arr.length < max) setField(key, [...arr, val]);
    else toast.info(`You can pick up to ${max}.`);
  };
  const addOtherMulti = (key, val, max) => {
    if (!val.trim()) return;
    const arr = Array.isArray(data[key]) ? data[key] : [];
    const tagged = `Other: ${val.trim()}`;
    if (arr.includes(tagged)) return;
    if (arr.length >= max) { toast.info(`You can pick up to ${max}.`); return; }
    setField(key, [...arr, tagged]);
    setOtherInput("");
  };

  // Chips helpers
  const addChip = (key, val) => {
    if (!val || !val.trim()) return;
    const arr = data[key] || [];
    if (arr.includes(val)) return;
    setField(key, [...arr, val]);
    setChipInput("");
  };
  const removeChip = (key, val) => setField(key, (data[key] || []).filter((v) => v !== val));

  const submit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...data,
        child_age: parseInt(data.child_age, 10),
        gifted: data.gifted === "Yes" || data.gifted === "Maybe",
      };
      const { data: report } = await api.post("/assessment", payload);
      toast.success("Your report is ready!");
      navigate(`/report/${report.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not generate report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (current.type === "intro" || current.type === "submit") return true;
    if (current.type === "chips") return (data[current.key] || []).length > 0;
    if (current.type === "multi") return (Array.isArray(data[current.key]) && data[current.key].length > 0);
    const v = data[current.key];
    return v !== undefined && v !== null && v !== "";
  };

  const noteKey = `${current.key}_note`;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-16 min-h-[80vh]">
      {/* progress */}
      <div className="mb-10">
        <div className="flex justify-between text-xs text-brand-muted mb-2">
          <span>{stepLabel}</span>
          <span>{progress}%</span>
        </div>
        <div className="h-2 bg-brand-divider rounded-full overflow-hidden">
          <motion.div className="h-full bg-brand-green" animate={{ width: `${progress}%` }} transition={{ duration: 0.4 }} />
        </div>
      </div>

      <AnimatePresence mode="wait">
        <motion.div
          key={step}
          initial={{ opacity: 0, x: 20 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -20 }}
          transition={{ duration: 0.3 }}
        >
          {current.type === "intro" && (
            <div className="text-center py-10">
              <Sparkles size={40} className="text-brand-green mx-auto mb-4" />
              <h1 className="text-4xl font-semibold text-brand-blue">A few warm questions.</h1>
              <p className="mt-4 text-lg text-brand-muted max-w-lg mx-auto">
                Takes about 15 minutes. There are no wrong answers — many questions accept multiple choices and free-text notes so you can share exactly what's on your mind.
              </p>
              <button data-testid="assessment-begin-btn" onClick={next} className="btn-primary mt-8">
                Let's begin <ArrowRight size={16} />
              </button>
            </div>
          )}

          {current.type === "text" && (
            <div>
              <h2 className="text-3xl font-semibold text-brand-blue">{current.label}</h2>
              {current.subtitle && <p className="mt-2 text-brand-muted">{current.subtitle}</p>}
              <input
                data-testid={`assessment-${current.key}-input`}
                autoFocus type="text"
                className="field mt-6 text-lg"
                placeholder={current.placeholder}
                value={data[current.key] || ""}
                onChange={(e) => setField(current.key, e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && canProceed() && next()}
              />
            </div>
          )}

          {current.type === "number" && (
            <div>
              <h2 className="text-3xl font-semibold text-brand-blue">{current.label}</h2>
              <input
                data-testid={`assessment-${current.key}-input`}
                autoFocus type="number"
                min={current.min} max={current.max}
                className="field mt-6 text-lg"
                placeholder={current.placeholder}
                value={data[current.key] || ""}
                onChange={(e) => setField(current.key, e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && canProceed() && next()}
              />
            </div>
          )}

          {current.type === "select" && (
            <div>
              <h2 className="text-3xl font-semibold text-brand-blue">{current.label}</h2>
              {current.subtitle && <p className="mt-2 text-brand-muted">{current.subtitle}</p>}
              <div className="mt-6 grid sm:grid-cols-2 gap-3">
                {current.options.map((opt) => {
                  const active = data[current.key] === opt;
                  return (
                    <button
                      key={opt}
                      data-testid={`assessment-${current.key}-${opt.replace(/\s+/g,'-').toLowerCase()}`}
                      onClick={() => { setField(current.key, opt); setTimeout(next, 200); }}
                      className={`text-left px-5 py-4 rounded-2xl border-2 transition-all ${
                        active
                          ? "border-brand-green bg-brand-green-soft text-brand-blue"
                          : "border-brand-border bg-white hover:border-brand-blue text-brand-blue"
                      }`}
                    >
                      {opt}
                    </button>
                  );
                })}
              </div>
              {current.allow_other && (
                <div className="mt-4 flex gap-2 items-center">
                  <span className="text-sm text-brand-muted whitespace-nowrap">Other:</span>
                  <input
                    className="field"
                    placeholder="Type your own answer..."
                    value={typeof data[current.key] === "string" && data[current.key].startsWith("Other: ") ? data[current.key].slice(7) : otherInput}
                    onChange={(e) => setOtherInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && otherInput.trim()) {
                        setField(current.key, `Other: ${otherInput.trim()}`);
                        setTimeout(next, 200);
                      }
                    }}
                    data-testid={`assessment-${current.key}-other`}
                  />
                  <button
                    className="btn-outline shrink-0"
                    onClick={() => {
                      if (otherInput.trim()) {
                        setField(current.key, `Other: ${otherInput.trim()}`);
                        setTimeout(next, 200);
                      }
                    }}
                  >Use</button>
                </div>
              )}
            </div>
          )}

          {current.type === "multi" && (
            <div>
              <h2 className="text-3xl font-semibold text-brand-blue">{current.label}</h2>
              {current.subtitle && <p className="mt-2 text-brand-muted">{current.subtitle}</p>}
              {(() => {
                const selected = Array.isArray(data[current.key]) ? data[current.key] : [];
                const atMax = selected.length >= current.max;
                return (
                  <>
                    <div className="mt-3 text-xs text-brand-green font-medium">
                      Selected {selected.length} / {current.max}
                    </div>
                    <div className="mt-4 grid sm:grid-cols-2 gap-3">
                      {current.options.map((opt) => {
                        const on = selected.includes(opt);
                        const disabled = !on && atMax;
                        return (
                          <button
                            key={opt}
                            data-testid={`assessment-${current.key}-${opt.replace(/\s+/g,'-').toLowerCase()}`}
                            disabled={disabled}
                            onClick={() => toggleMulti(current.key, opt, current.max)}
                            className={`text-left px-5 py-3.5 rounded-2xl border-2 transition-all ${
                              on
                                ? "border-brand-green bg-brand-green-soft text-brand-blue"
                                : disabled
                                ? "border-brand-border bg-brand-divider text-brand-muted cursor-not-allowed opacity-50"
                                : "border-brand-border bg-white hover:border-brand-blue text-brand-blue"
                            }`}
                          >
                            {opt}
                          </button>
                        );
                      })}
                    </div>
                    {current.allow_other && (
                      <div className="mt-4 flex gap-2 items-center">
                        <span className="text-sm text-brand-muted whitespace-nowrap">Add other:</span>
                        <input
                          className="field"
                          placeholder="Type your own and press Add..."
                          value={otherInput}
                          onChange={(e) => setOtherInput(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addOtherMulti(current.key, otherInput, current.max); }}}
                          data-testid={`assessment-${current.key}-other`}
                        />
                        <button className="btn-outline shrink-0" onClick={() => addOtherMulti(current.key, otherInput, current.max)}>Add</button>
                      </div>
                    )}
                    {selected.length > 0 && (
                      <div className="mt-4 flex flex-wrap gap-2">
                        {selected.map((v) => (
                          <span key={v} className="chip">
                            {v}
                            <button onClick={() => toggleMulti(current.key, v, current.max)} className="ml-1 text-brand-green">×</button>
                          </span>
                        ))}
                      </div>
                    )}
                  </>
                );
              })()}
            </div>
          )}

          {current.type === "chips" && (
            <div>
              <h2 className="text-3xl font-semibold text-brand-blue">{current.label}</h2>
              {current.subtitle && <p className="mt-2 text-brand-muted">{current.subtitle}</p>}
              <div className="mt-6 flex flex-wrap gap-2">
                {current.suggestions.map((s) => {
                  const on = (data[current.key] || []).includes(s);
                  return (
                    <button
                      key={s}
                      data-testid={`assessment-${current.key}-chip-${s.toLowerCase()}`}
                      onClick={() => (on ? removeChip(current.key, s) : addChip(current.key, s))}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-all ${
                        on
                          ? "bg-brand-blue text-white border-brand-blue"
                          : "bg-white text-brand-blue border-brand-border hover:border-brand-blue"
                      }`}
                    >
                      {s}
                    </button>
                  );
                })}
              </div>
              <div className="mt-6 flex gap-2">
                <input
                  className="field"
                  placeholder="Add your own..."
                  value={chipInput}
                  onChange={(e) => setChipInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addChip(current.key, chipInput.trim()))}
                />
                <button onClick={() => addChip(current.key, chipInput.trim())} className="btn-outline shrink-0">Add</button>
              </div>
              {(data[current.key] || []).length > 0 && (
                <div className="mt-4 flex flex-wrap gap-2">
                  {data[current.key].map((v) => (
                    <span key={v} className="chip">
                      {v}
                      <button onClick={() => removeChip(current.key, v)} className="ml-1 text-brand-green">×</button>
                    </span>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Add a note — for every non-intro/submit question */}
          {current.type !== "intro" && current.type !== "submit" && (
            <div className="mt-8">
              {!showNote[current.key] ? (
                <button
                  data-testid={`assessment-${current.key}-add-note`}
                  onClick={() => setShowNote({ ...showNote, [current.key]: true })}
                  className="text-sm text-brand-green hover:text-brand-blue flex items-center gap-1.5"
                >
                  <Plus size={14} /> Add a note (anything extra you want to share)
                </button>
              ) : (
                <div>
                  <label className="text-sm text-brand-blue flex items-center gap-1.5 mb-2">
                    <MessageSquare size={14} className="text-brand-green" /> Your note
                  </label>
                  <textarea
                    data-testid={`assessment-${current.key}-note`}
                    className="field resize-none"
                    rows={3}
                    placeholder="Anything extra we should know for this question..."
                    value={data[noteKey] || ""}
                    onChange={(e) => setField(noteKey, e.target.value)}
                  />
                </div>
              )}
            </div>
          )}

          {current.type === "submit" && (
            <div className="text-center py-10">
              <div className="w-16 h-16 rounded-2xl bg-brand-orange-soft text-brand-orange mx-auto mb-6 flex items-center justify-center">
                <Sparkles size={28} />
              </div>
              <h2 className="text-3xl font-semibold text-brand-blue">Ready to generate your report?</h2>
              <p className="mt-4 text-brand-muted max-w-md mx-auto">
                We'll craft a personalized, unbiased report for {data.child_name || "your child"} — about 30 seconds.
              </p>
              <button data-testid="assessment-submit-btn" onClick={submit} disabled={loading} className="btn-primary mt-8 disabled:opacity-60">
                {loading ? (<><Loader2 className="animate-spin" size={16} /> Generating...</>) : (<>Generate my report <ArrowRight size={16} /></>)}
              </button>
            </div>
          )}
        </motion.div>
      </AnimatePresence>

      {/* footer nav */}
      {current.type !== "intro" && current.type !== "submit" && (
        <div className="mt-10 flex justify-between">
          <button data-testid="assessment-back-btn" onClick={prev} className="btn-ghost" disabled={step === 0}>
            <ArrowLeft size={16} /> Back
          </button>
          <button data-testid="assessment-next-btn" onClick={next} disabled={!canProceed()} className="btn-primary disabled:opacity-40">
            Continue <ArrowRight size={16} />
          </button>
        </div>
      )}
    </div>
  );
}
